import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Loader2, Mail, ShoppingCart, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { auth } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';

const VerifyEmailPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(5);
  const [userRole, setUserRole] = useState('buyer');
  const [sellerStatus, setSellerStatus] = useState(null);
  const token = searchParams.get('token');
  const currentLang = i18n.language || 'tr';

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage(t('auth.invalidVerificationLink'));
        return;
      }

      try {
        const response = await auth.verifyEmail(token);
        setStatus('success');
        setMessage(response.data?.message || t('auth.emailVerified'));
        
        // Auto-login: Save token and user data
        if (response.data?.token && response.data?.user) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          setUser(response.data.user);
          
          // Store user role and seller status for redirect logic
          const role = response.data.user.role || 'buyer';
          const sellerStat = response.data.user.seller_status;
          setUserRole(role);
          setSellerStatus(sellerStat);
          
          // Start countdown for redirect
          let count = 5;
          const timer = setInterval(() => {
            count--;
            setCountdown(count);
            if (count <= 0) {
              clearInterval(timer);
              // Redirect based on role
              if (role === 'seller') {
                // Seller redirect logic
                if (sellerStat === 'approved') {
                  navigate(`/${currentLang}/seller`);
                } else {
                  // Pending approval - go to pending page
                  navigate(`/${currentLang}/seller-registration-pending`);
                }
              } else if (role === 'admin') {
                navigate(`/${currentLang}/admin`);
              } else {
                // Buyer - check cart or go home
                const pendingCart = localStorage.getItem('pendingCart');
                if (pendingCart) {
                  navigate(`/${currentLang}/cart`);
                } else {
                  navigate(`/${currentLang}`);
                }
              }
            }
          }, 1000);
        }
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || t('auth.verificationFailed'));
      }
    };

    verifyEmail();
  }, [token, t, setUser, navigate, currentLang]);

  // Get appropriate redirect button based on role
  const getRedirectButton = () => {
    if (userRole === 'seller') {
      if (sellerStatus === 'approved') {
        return (
          <Button 
            className="w-full rounded-full bg-green-600 hover:bg-green-700" 
            onClick={() => navigate(`/${currentLang}/seller`)}
          >
            <Store className="w-4 h-4 mr-2" />
            {t('auth.goToSellerDashboard', 'Satıcı Paneline Git')}
          </Button>
        );
      } else {
        return (
          <Button 
            className="w-full rounded-full bg-orange-500 hover:bg-orange-600" 
            onClick={() => navigate(`/${currentLang}/seller-registration-pending`)}
          >
            <Store className="w-4 h-4 mr-2" />
            {t('auth.viewApprovalStatus', 'Onay Durumunu Görüntüle')}
          </Button>
        );
      }
    }
    return (
      <Button 
        className="w-full rounded-full" 
        onClick={() => navigate(`/${currentLang}/cart`)}
      >
        <ShoppingCart className="w-4 h-4 mr-2" />
        {t('auth.continueShoppingNow')}
      </Button>
    );
  };

  // Get success message based on role
  const getSuccessSubtitle = () => {
    if (userRole === 'seller') {
      if (sellerStatus === 'approved') {
        return t('auth.sellerEmailVerifiedApproved', 'Hesabınız onaylandı. Satıcı panelinize yönlendiriliyorsunuz.');
      } else {
        return t('auth.sellerEmailVerifiedPending', 'E-postanız doğrulandı. Satıcı başvurunuz admin onayı bekliyor.');
      }
    }
    return message;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/10 p-4">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-2xl shadow-xl border p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
              </div>
              <h1 className="text-2xl font-bold mb-2">{t('auth.verifyingEmail')}</h1>
              <p className="text-muted-foreground">{t('auth.pleaseWait')}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-green-600 mb-2">{t('auth.emailVerifiedTitle')}</h1>
              <p className="text-muted-foreground mb-4">{getSuccessSubtitle()}</p>
              
              {/* Show seller status badge if seller */}
              {userRole === 'seller' && sellerStatus !== 'approved' && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-700">
                    ⏳ {t('auth.sellerPendingNote', 'Satıcı hesabınız admin onayı bekliyor. Onaylandığında size e-posta ile bilgi vereceğiz.')}
                  </p>
                </div>
              )}
              
              <p className="text-sm text-muted-foreground mb-6">
                {t('auth.redirectingIn', { seconds: countdown })}
              </p>
              {getRedirectButton()}
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-red-600 mb-2">{t('auth.verificationFailedTitle')}</h1>
              <p className="text-muted-foreground mb-6">{message}</p>
              <div className="space-y-3">
                <Link to={`/${currentLang}/resend-verification`}>
                  <Button variant="outline" className="w-full rounded-full">
                    <Mail className="w-4 h-4 mr-2" />
                    {t('auth.resendVerification')}
                  </Button>
                </Link>
                <Link to={`/${currentLang}/login`}>
                  <Button variant="ghost" className="w-full rounded-full">
                    {t('auth.backToLogin')}
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
